/**
 * An AbortSignal that fires after `ms`.
 *
 * `AbortSignal.timeout` only arrived in Safari 16, and calling it where it is missing throws
 * before fetch even starts — so on an older iPhone every form and every chat request failed
 * with no network call at all. The fallback aborts with a DOMException named "TimeoutError"
 * where the browser can carry a reason; where it cannot, fetch rejects with an AbortError, and
 * `isTimeoutError` treats both the same.
 */
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => {
    try {
      controller.abort(new DOMException("signal timed out", "TimeoutError"));
    } catch {
      controller.abort();
    }
  }, ms);
  return controller.signal;
}

/** True for the error fetch rejects with when a `timeoutSignal` fires, native or fallback. */
export function isTimeoutError(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("name" in error)) return false;
  const name = (error as { name: unknown }).name;
  return name === "TimeoutError" || name === "AbortError";
}
